import { Endpoint, Webhook } from '@square/ignition';
import ArrowLeft from '@squareup/dex-icons/market/arrow/Left';
import { ReferenceHrefBuilders } from '@squareup/dex-tech-utils-routing';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import {
  NavigationList,
  NavigationItem,
  Link,
  NavigationItemCollapse,
} from '@squareup/dex-ui';
import { useIsMarketComponentLoaded } from '@squareup/dex-ui-hooks';
import { Box, Paragraph30 } from '@squareup/dex-ui-shared-base';
import { commonIconStyles } from '@squareup/dex-ui-shared-icon-styles';
import { MarketButton } from '@squareup/dex-ui-shared-market';
import { NavigationMobileToggleProps } from '@squareup/dex-ui-shared-mobile-nav';
import {
  filterForDeprecatedGraphObjects,
  filterForValidGraphObjects,
  filterForValidV1GraphObjects,
  toWebhookPathParam,
} from '@squareup/dex-utils-oas';
import { breakHtmlForAll, humanizeName } from '@squareup/dex-utils-text';
import clsx from 'clsx';
import parse from 'html-react-parser';
import React, { useMemo, useRef } from 'react';

import { ReferenceStickyNavigation } from '../ReferenceStickyNavigation';

import styles from './reference-api-detail-navigation.module.css';

interface ReferenceNavigationProps {
  activeItem?: Endpoint | Webhook | undefined;
  activeVersion: string;
  isWebhookNav: boolean;
  isDeprecatedNav: boolean;
}

interface WebhookEndpointNavItemProps {
  item: Endpoint | Webhook;
  apiPath: string | undefined;
  activeVersion: string;
  activeItemId: string | undefined;
}

const WebhookEndpointNavItem = ({
  item,
  apiPath,
  activeVersion,
  activeItemId,
}: WebhookEndpointNavItemProps) => {
  const { name, id } = item;

  let itemId = id;
  // Default to the deprecated page
  let href = ReferenceHrefBuilders.versionDeprecated({
    square_version: activeVersion,
  });

  let itemName: string = '';
  if (item instanceof Endpoint) {
    href = ReferenceHrefBuilders.versionApiEndpoint({
      api: apiPath || '',
      endpoint: id,
      square_version: activeVersion,
    });
    itemName = humanizeName(name);
  } else if (item instanceof Webhook) {
    itemId = name;
    href = ReferenceHrefBuilders.versionApiWebhook({
      api: apiPath || ' ',
      webhook: toWebhookPathParam(item),
      square_version: activeVersion,
    });
    itemName = breakHtmlForAll(name);
  }

  return (
    <NavigationItem
      trackingId={`navigate-to-reference-api-details-${id}`}
      isSubItem={true}
      key={id}
      href={href}
      text={parse(itemName)}
      testId={'reference-nav-item'}
      isActive={activeItemId === itemId}
    ></NavigationItem>
  );
};

const ReferenceAPIDetailNavigation = ({
  className,
  activeVersion,
  activeItem,
  isWebhookNav,
  isDeprecatedNav,
  ...rest
}: ReferenceNavigationProps &
  NullableClassName &
  NavigationMobileToggleProps) => {
  const api = activeItem?.api;
  const webhooks = api?.webhooks;
  const endpoints = api?.endpoints;

  const apiPath = `${api?.id}-api`;

  function getHrefBuilder() {
    if (isWebhookNav) {
      if (isDeprecatedNav) {
        return ReferenceHrefBuilders.versionDeprecatedWebhooks({
          square_version: activeVersion,
        });
      } else {
        return ReferenceHrefBuilders.versionApiWebhookIndex({
          api: apiPath,
          square_version: activeVersion,
        });
      }
    } else {
      if (isDeprecatedNav) {
        return ReferenceHrefBuilders.versionDeprecated({
          square_version: activeVersion,
        });
      } else {
        return ReferenceHrefBuilders.versionApi({
          api: apiPath,
          square_version: activeVersion,
        });
      }
    }
  }

  const hrefBuilder = getHrefBuilder();

  const filterObjects = useMemo(() => {
    return activeItem?.isDeprecated
      ? filterForDeprecatedGraphObjects
      : filterForValidGraphObjects;
  }, [activeItem]);

  const validEndpoints = filterObjects(endpoints);
  const validWebhooks = filterObjects(webhooks);
  const v1Endpoints = filterForValidV1GraphObjects(endpoints);
  const v1Webhooks = filterForValidV1GraphObjects(webhooks);

  const hasEndpoint = validEndpoints.length > 0 || v1Endpoints.length > 0;
  const hasWebhooks = validWebhooks.length > 0 || v1Webhooks.length > 0;

  const ref = useRef<globalThis.HTMLMarketButtonElement>(null);
  const [isMarketComponentLoaded, onMarketRef] = useIsMarketComponentLoaded(
    ref,
    'market-button'
  );

  return (
    <ReferenceStickyNavigation
      testId="reference-api-detail-navigation"
      className={clsx(styles.nav, className)}
      {...rest}
    >
      <Box
        margin={{ left: '1x', bottom: '3x' }}
        className={styles['back-button-wrapper']}
      >
        <Link
          trackingId="navigate-to-reference-api-back"
          testId="navigate-to-reference-api-back"
          omitFocus={true}
          className={styles['back-button-link']}
          href={hrefBuilder}
        >
          <MarketButton
            aria-label="Back"
            trackingId="navigate-to-reference-api-back"
            rank="secondary"
            size="small"
            className={clsx(
              styles['back-button'],
              !isMarketComponentLoaded && styles.hidden,
              className
            )}
            ref={onMarketRef}
          >
            <ArrowLeft
              slot="icon"
              className={commonIconStyles['icon-color']}
            ></ArrowLeft>
          </MarketButton>
        </Link>
      </Box>
      <NavigationList isSubNavigation={true} enableOverflowShimmer={true}>
        {hasEndpoint && (
          <Paragraph30
            weight="semi-bold"
            margin={{ left: '1x', top: '2x', bottom: '1x' }}
            className={styles['title']}
          >
            {activeItem?.api?.name}
          </Paragraph30>
        )}
        {validEndpoints?.map((endpoint) => {
          return (
            <WebhookEndpointNavItem
              key={endpoint.id}
              item={endpoint}
              apiPath={apiPath}
              activeVersion={activeVersion}
              activeItemId={activeItem?.id}
            ></WebhookEndpointNavItem>
          );
        })}
        {v1Endpoints.length > 0 && (
          <NavigationItemCollapse
            text="V1 endpoints"
            trackingId="v1-endpoints-collapse"
            forceOpen={Boolean(
              v1Endpoints.some((endpoint) => endpoint.id === activeItem?.id)
            )}
          >
            {v1Endpoints?.map((endpoint) => {
              return (
                <WebhookEndpointNavItem
                  key={endpoint.id}
                  item={endpoint}
                  apiPath={apiPath}
                  activeVersion={activeVersion}
                  activeItemId={activeItem?.id}
                ></WebhookEndpointNavItem>
              );
            })}
          </NavigationItemCollapse>
        )}
        {hasWebhooks && (
          <Paragraph30
            weight="semi-bold"
            margin={{ left: '1x', top: '2x', bottom: '1x' }}
          >
            Webhooks
          </Paragraph30>
        )}
        {validWebhooks?.map((webhook) => {
          return (
            <WebhookEndpointNavItem
              key={webhook.id}
              item={webhook}
              apiPath={apiPath}
              activeVersion={activeVersion}
              activeItemId={activeItem?.name}
            ></WebhookEndpointNavItem>
          );
        })}
        {v1Webhooks.length > 0 && (
          <NavigationItemCollapse
            text="V1 webhooks"
            trackingId="v1-webhooks-collapse"
            forceOpen={Boolean(
              v1Webhooks.some((webhook) => webhook.id === activeItem?.id)
            )}
          >
            {v1Webhooks?.map((webhook) => {
              return (
                <WebhookEndpointNavItem
                  key={webhook.id}
                  item={webhook}
                  apiPath={apiPath}
                  activeVersion={activeVersion}
                  activeItemId={activeItem?.name}
                ></WebhookEndpointNavItem>
              );
            })}
          </NavigationItemCollapse>
        )}
      </NavigationList>
    </ReferenceStickyNavigation>
  );
};

export { ReferenceAPIDetailNavigation };
